import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

import { useSidebarSummaryContext } from '../../../../../../SidebarSummary.context';
import type { RankingAnimationProps } from '../../RankingAnimation';
import {
  StyledBasicTitle,
  StyledBiggerSubtitle,
  StyledCanvas,
  StyledImage,
  Wrapper,
} from './SuccessRankingAnimation.styled';

const DEFAULT_CONFETTI_DURATION = 10;

export const SuccessRankingAnimation = ({
  hideAnimation,
  onAnimationEnd,
  isFullWidthActive,
}: RankingAnimationProps) => {
  const {
    state: { isOpen },
    config: { getTranslation },
  } = useSidebarSummaryContext();

  const canvasRef = useRef<HTMLCanvasElement>();

  let interval: NodeJS.Timeout;

  const runConfetti = () => {
    const myConfetti = confetti.create(canvasRef.current, {
      resize: true,
    });

    const duration = DEFAULT_CONFETTI_DURATION * 1000;
    const animationEnd = Date.now() + duration;

    const defaults = {
      startVelocity: 20,
      spread: 360,
      ticks: 60,
      zIndex: 10000,
    };

    myConfetti({
      ...defaults,
      disableForReducedMotion: true,
    });

    interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 5 * (timeLeft / duration);

      myConfetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random(), y: Math.random() * 0.2 },
        disableForReducedMotion: true,
      });

      return undefined;
    }, 250);
  };

  useEffect(() => {
    if (isOpen) {
      runConfetti();
    } else {
      clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAnimationEnd = () => {
    if (onAnimationEnd && hideAnimation) {
      onAnimationEnd();
    }
  };

  return (
    <Wrapper $activeHideAnimation={hideAnimation} onAnimationEnd={handleAnimationEnd}>
      <StyledCanvas
        ref={canvasRef as RefObject<HTMLCanvasElement>}
        $show={isOpen}
        $isFullWidthActive={isFullWidthActive}
      />
      <StyledImage
        src="https://static.genially.com/genially/activities/victory"
        $activeAnimation={isOpen}
      />
      <StyledBasicTitle $activeAnimation={isOpen}>
        {getTranslation('__new.content.interactiveQuestion.sidebar.correctAnswer.title')}
      </StyledBasicTitle>
      <StyledBiggerSubtitle $activeAnimation={isOpen}>
        {getTranslation(
          '__new.content.interactiveQuestion.sidebar.correctAnswer.subtitle',
        )}
      </StyledBiggerSubtitle>
    </Wrapper>
  );
};
